/**
 * Manage adjustments component (manage-adjustments.component.ts).
 *
 * @author Ryan Sweeney <rsweeney@buildcentrix.com>
 * @copyright (c) 2022-2024 Build Centrix
 */
import {
  AfterViewInit,
  Component,
  Inject,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { WdOrdersService } from '../../../services/orders/wd-orders.service';
import { WdLocalEnvironmentService } from '../../../services/environment/wd-local-environment.service';
import { NgForm } from '@angular/forms';
import { WdOrderAdjustmentsService } from '../../../services/orders/adjustments/wd-order-adjustments.service';
import { WdOrderFulfillmentGroupOptionsService } from '../../../services/orders/options/fulfillment/groups/wd-order-fulfillment-group-options.service';
import { BehaviorSubject } from 'rxjs';
import { WdOrderGaugeOptionsService } from '../../../services/orders/options/gauges/wd-order-gauge-options.service';
import { WdOrderMaterialOptionsService } from '../../../services/orders/options/materials/wd-order-material-options.service';
import { WdLocalSharedFunctionsService } from '../../../services/shared/wd-local-shared-functions.service';
import {
  IFulfillmentGroupOption,
  IGaugeOption,
  IMaterialOption,
  IOrderAdjustmentModel,
} from '../../../interfaces/orders/order-interfaces';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-manage-adjustments',
  templateUrl: './order-adjustments.component.html',
  styleUrls: ['./order-adjustments.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class OrderAdjustmentsComponent implements OnInit, AfterViewInit {
  // Form
  @ViewChild('adjustmentsForm', { static: true })
  public adjustmentsForm!: NgForm;

  // Title
  title: string;

  // Inputs
  adjustment: IOrderAdjustmentModel;
  adjustmentUntouched: string;
  group: any;
  gaugeOptions = new BehaviorSubject<Array<IGaugeOption>>([]);
  groupOptions = new BehaviorSubject<Array<IFulfillmentGroupOption>>([]);
  materialOptions = new BehaviorSubject<Array<IMaterialOption>>([]);
  order: { index: string; number: number };

  // Booleans
  isChanges = false;
  isEditMode = false;
  isLoading = false;

  // Callbacks
  onSuccess: any;

  // Target
  targetAdjustmentKey: string;

  /**
   * Constructor
   * @since 4.36.0
   */
  constructor(
    private translate: TranslateService,
    public wdLocalSharedFunctions: WdLocalSharedFunctionsService,
    public wdOrdersService: WdOrdersService,
    public wdOrderAdjustmentsService: WdOrderAdjustmentsService,
    public wdOrderGaugeOptionsService: WdOrderGaugeOptionsService,
    public wdOrderFulfillmentGroupOptionsService: WdOrderFulfillmentGroupOptionsService,
    public wdOrderMaterialOptionsService: WdOrderMaterialOptionsService,
    public dialogRef: MatDialogRef<OrderAdjustmentsComponent>,
    public wdEnvironmentService: WdLocalEnvironmentService,
    @Inject('wdErrorService') public wdErrorService: any,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    // Order Index
    this.order = data.order;
    // Target adjustment (optional)
    this.targetAdjustmentKey = data.target;
    // Defaults
    this.adjustment = OrderAdjustmentsComponent.initializeAdjustment(
      this.data.adjustment,
    );
    // Capture Changes
    this.adjustmentUntouched = JSON.stringify(this.adjustment);
    // Functions
    this.onSuccess = data.onSuccessCallback;
  }

  /**
   * Initialize Adjustment
   * @since 4.36.0
   */
  static initializeAdjustment(adjustment: any, group?: any) {
    return {
      index: adjustment ? adjustment.index : null,
      id: adjustment ? adjustment.id : null,
      label: adjustment ? adjustment.label : '',
      description: adjustment ? adjustment.description : '',
      gauge: adjustment ? adjustment.gauge : null,
      group: group ? group : null,
      cost: adjustment ? adjustment.cost : { value: 0 },
      material: adjustment ? adjustment.material : null,
      price: adjustment ? adjustment.price : { value: 0 },
      shopLabour: adjustment ? adjustment.shopLabour : { value: 0 },
      weight: adjustment ? adjustment.weight : { value: 0 },
    };
  }

  /**
   * Initialize gauge data
   * @since 4.61.0
   */
  private async initGaugeData() {
    // Get gauge options
    const gaugeOptions = await this.wdOrderGaugeOptionsService.getGaugeOptions();

    // Set selected gauge
    const targetGauge: any = this.data.adjustment?.material?.gauge;
    const selectedGauge: IGaugeOption = targetGauge
      ? gaugeOptions.find(
          (gauge: IGaugeOption) => gauge.value === targetGauge.value,
        )
      : null;

    // Set gauge options
    this.gaugeOptions.next(gaugeOptions);

    // Return selected gauge
    return selectedGauge;
  }

  /**
   * Initialize fulfillment group data
   * @since 4.59.0
   */
  private async initGroupData() {
    // Get fulfillment group options
    const groupOptions = await this.wdOrderFulfillmentGroupOptionsService.getFulfillmentGroupOptions();

    // Set selected fulfillment group
    const targetGroup: any = this.data.group;
    const selectedGroup: IFulfillmentGroupOption = targetGroup
      ? groupOptions.find(
          (grp: IFulfillmentGroupOption) => grp.id === targetGroup.index,
        )
      : null;

    // Set fulfillment group options
    this.groupOptions.next(groupOptions);

    // Return selected group
    return selectedGroup;
  }

  /**
   * Initialize material data
   * @since 4.61.0
   */
  private async initMaterialData() {
    // Get material options
    const materialOptions = await this.wdOrderMaterialOptionsService.getMaterialOptions();

    // Set selected material
    const targetMaterial: any = this.data.adjustment?.material;
    const selectedMaterial: IMaterialOption = targetMaterial
      ? materialOptions.find(
          (mat: IMaterialOption) => mat.id === targetMaterial.id,
        )
      : null;

    // Set material options
    this.materialOptions.next(materialOptions);

    // Return selected material
    return selectedMaterial;
  }

  /**
   * Initialize the modal title based on context (edit/add mode, adjustment type, and group).
   */
  initModalTitle(): void {
    // Add OR Edit
    const isEdit = this.isEditMode;
    const actionKey = isEdit ? 'actions.editType' : 'actions.addType';
    // Adjustment target (optional) - Weight, Price, Labour, etc.
    const target = this.targetAdjustmentKey;
    const targetLabel = target
      ? this.translate.instant(`order.columns.${target}.title`)
      : '';
    // Fulfillment group (optional) - Rectangular, Round, etc.
    const groupLabel = this.adjustment.group?.label || '';
    // Adjustment label
    const adjustmentLabel = this.translate.instant(
      'order.adjustment.singular.title',
    );
    // Order Number
    const orderNumber =
      ' - ' +
      this.translate.instant('order.details.number.title') +
      this.order.number;
    // Combine strings
    const titleType = [groupLabel, targetLabel, adjustmentLabel, orderNumber]
      .filter(Boolean)
      .join(' ');
    // Set title
    this.title = this.translate.instant(actionKey, { type: titleType });
  }

  /**
   * On Changes to Inputs
   * @since 4.36.0
   */
  checkForChanges(): void {
    this.isChanges =
      this.adjustmentUntouched !== JSON.stringify(this.adjustment);
  }

  /**
   * On Modal Dismiss
   * @since 4.36.0
   */
  onDismiss(): void {
    // Close the dialog, return false
    this.dialogRef.close(false);
  }

  /**
   * After View Init
   * @since 4.36.0
   */
  ngAfterViewInit() {
    // clear anything at the parent form level
    this.adjustmentsForm?.reset(this.adjustment);
  }

  /**
   * On Init
   * @since 4.36.0
   */
  async ngOnInit() {
    // Start Loader
    this.isLoading = true;
    // Set Add/Edit Mode
    this.isEditMode = !!this.adjustment?.index || !!this.adjustment?.id;
    // Initialize gauge data and set adjustment gauge
    this.adjustment.gauge = await this.initGaugeData();
    // Initialize fulfillment group data and set adjustment fulfillment group
    this.adjustment.group = await this.initGroupData();
    // Initialize material data and set adjustment material
    this.adjustment.material = await this.initMaterialData();
    // Init modal title
    this.initModalTitle();
    // Stop Loader
    this.isLoading = false;
  }

  /**
   * On Modal Submit
   * @since 4.36.0
   */
  async onSubmit(adjustment: any): Promise<void> {
    // Start Loader
    this.isLoading = true;
    try {
      if (this.isEditMode) {
        await this.wdOrderAdjustmentsService.editOrderAdjustment(
          this.order.index,
          adjustment,
        );
      } else {
        await this.wdOrderAdjustmentsService.addOrderAdjustment(
          this.order.index,
          adjustment,
        );
      }
      // Close the dialog, return false
      this.onSuccess();
      this.dialogRef.close(false);
    } finally {
      // Stop Loader
      this.isLoading = false;
    }
  }
}
